/**
 * AdminGoogleSearchCard
 *
 * Free-text Google web search powered by SerpAPI. Admin types any keyword
 * (e.g. "wedding dance chennai instagram phone number"), sees the live
 * Google results with extracted phone numbers, ticks the rows whose phones
 * they want, and one-click saves them to the Contact Book tagged with the
 * keyword as a segment.
 *
 * Web-only — hidden from the APK build (see /app/memory/APP_WEB_ONLY_FEATURES.md).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, Spinner, Phone, FloppyDisk, CheckCircle } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGoogleSearchCard = () => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [results, setResults] = useState(null);
  // Set of "rowIndex:phone" strings — we keep selection at the phone level
  // because one result row can yield 2 phones and the admin may want only 1.
  const [selected, setSelected] = useState(new Set());
  const [quota, setQuota] = useState(null);
  // Filter: when true, only show rows that contain at least one phone
  // *not* already in the Contact Book — i.e., skip duplicates that have
  // nothing new to offer. Default ON so the admin doesn't waste time
  // re-reviewing the same contacts after multiple searches.
  const [hideSaved, setHideSaved] = useState(true);

  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/google-search/status`,
        { withCredentials: true });
      setQuota(data.quota);
    } catch (_) { /* silent — status is informational only */ }
  };

  const runSearch = async (append = false) => {
    const kw = keyword.trim();
    if (kw.length < 2) {
      toast.error('Type at least 2 characters');
      return;
    }
    setLoading(true);
    if (!append) {
      setResults(null);
      setSelected(new Set());
    }
    try {
      // For pagination, start where the existing result list ended.
      const startAt = append && results ? results.next_start || 1 : 1;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/google-search/find`,
        { keyword: kw, num: 10, start: startAt },
        { withCredentials: true },
      );
      setQuota(data.quota);
      if (append && results) {
        // Append + dedupe new rows against existing ones using link as key.
        const existingLinks = new Set(results.rows.map(r => r.link));
        const newRows = data.rows.filter(r => !existingLinks.has(r.link));
        const merged = {
          ...data,
          rows: [...results.rows, ...newRows],
          total_results: results.total_results + newRows.length,
          total_phones: results.total_phones + newRows.reduce((s, r) => s + r.phones.length, 0),
          new_phones: results.new_phones + newRows.reduce((s, r) => s + r.new_phones.length, 0),
        };
        setResults(merged);
        // Auto-tick fresh phones from the newly-added rows.
        setSelected(prev => {
          const next = new Set(prev);
          newRows.forEach((row) => {
            const originalIdx = results.rows.length + newRows.indexOf(row);
            row.new_phones.forEach((p) => next.add(`${originalIdx}:${p}`));
          });
          return next;
        });
        toast.success(`+${newRows.length} more results (page ${Math.ceil(startAt / 10) + 1})`);
      } else {
        setResults(data);
        const initial = new Set();
        data.rows.forEach((row, idx) => {
          row.new_phones.forEach((p) => initial.add(`${idx}:${p}`));
        });
        setSelected(initial);
        toast.success(`${data.total_phones} phones found across ${data.total_results} results`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (rowIdx, phone) => {
    setSelected(prev => {
      const next = new Set(prev);
      const key = `${rowIdx}:${phone}`;
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveSelected = async () => {
    if (!results || selected.size === 0) {
      toast.info('Tick at least one phone first');
      return;
    }
    const contacts = [];
    selected.forEach((key) => {
      const [rowIdxStr, phone] = key.split(':');
      const row = results.rows[Number(rowIdxStr)];
      if (row) {
        contacts.push({
          phone,
          name: row.business_name,
          source: row.source,
        });
      }
    });
    setSavingAll(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/google-search/save-selection`,
        { keyword: results.keyword, contacts },
        { withCredentials: true },
      );
      toast.success(`Saved ${data.inserted} new + ${data.updated} updated → segment "${data.segment}"`);
      // Mark the just-saved phones as ✓ in the current view *without*
      // re-running the search — that would reset pagination back to page 1
      // and burn another SerpAPI quota call.
      const savedPhones = new Set(contacts.map(c => c.phone));
      setResults(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map(row => {
            const newlySaved = row.phones.filter(p => savedPhones.has(p));
            if (newlySaved.length === 0) return row;
            const alreadySaved = Array.from(new Set([...row.already_saved, ...newlySaved]));
            const newPhones = row.phones.filter(p => !alreadySaved.includes(p));
            return { ...row, already_saved: alreadySaved, new_phones: newPhones };
          }),
          new_phones: Math.max(0, prev.new_phones - savedPhones.size),
        };
      });
      // Clear the selection — those phones now display as ✓ saved.
      setSelected(prev => {
        const next = new Set();
        prev.forEach((key) => {
          const [, phone] = key.split(':');
          if (!savedPhones.has(phone)) next.add(key);
        });
        return next;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <Card className="rounded-lg border-[#E8E1D5]" data-testid="admin-google-search-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MagnifyingGlass size={20} weight="fill" className="text-brand-primary" />
          Google Web Search Finder
        </CardTitle>
        <CardDescription>
          Type any keyword — pulls real Google results, extracts phone numbers from titles + snippets,
          one-click saves to your Contact Book. Powered by SerpAPI.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label className="text-[10px] font-bold uppercase tracking-wider">
            Keyword (free-text)
          </Label>
          <div className="flex gap-2">
            <Input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) runSearch(false); }}
              placeholder="e.g. wedding dance chennai instagram phone number"
              className="rounded-none border-[#E8E1D5] h-10 flex-1"
              data-testid="google-search-keyword-input"
            />
            <Button onClick={() => runSearch(false)} disabled={loading}
              className="bg-brand-primary hover:opacity-90 text-sand-50 rounded-none h-10 px-5"
              data-testid="google-search-btn">
              {loading
                ? <><Spinner size={16} className="mr-2 animate-spin" /> Searching…</>
                : <><MagnifyingGlass size={16} className="mr-2" weight="fill" /> Search Google</>
              }
            </Button>
          </div>
          {quota && (
            <p className="text-[10px] text-text-secondary mt-1" data-testid="google-search-quota">
              SerpAPI quota: <strong>{quota.used}/{quota.cap}</strong> searches used this month
              ({quota.month}). Resets on the 1st.
            </p>
          )}
        </div>

        {results && (
          <div className="space-y-3" data-testid="google-search-results">
            <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-stone-50 border border-[#E8E1D5] rounded">
              <span className="text-xs font-bold text-text-primary">
                {results.total_results} results · {results.total_phones} phones · {results.new_phones} new
              </span>
              <span className="text-xs text-text-secondary">
                · {selected.size} ticked
              </span>
              <label className="flex items-center gap-1.5 text-xs text-text-primary cursor-pointer ml-2"
                data-testid="google-search-hide-saved-toggle">
                <input
                  type="checkbox"
                  checked={hideSaved}
                  onChange={(e) => setHideSaved(e.target.checked)}
                  className="cursor-pointer"
                />
                Hide already-saved
              </label>
              <Button onClick={saveSelected}
                disabled={savingAll || selected.size === 0}
                className="ml-auto bg-green-600 hover:bg-green-700 text-white rounded-none h-9 disabled:opacity-40"
                data-testid="google-search-save-btn">
                {savingAll
                  ? <><Spinner size={14} className="mr-2 animate-spin" /> Saving…</>
                  : <><FloppyDisk size={14} className="mr-2" weight="fill" /> Save {selected.size} phones to Contact Book</>}
              </Button>
            </div>

            {(() => {
              // Filtered view: drop rows that have no phones at all (useless
              // to the admin). When "Hide already-saved" is ON, also drop
              // rows whose phones are ALL already in the book. Rows that
              // mix saved + new phones survive so the admin can still grab
              // the new ones.
              const visibleRows = results.rows
                .map((row, originalIdx) => ({ row, originalIdx }))
                .filter(({ row }) => {
                  if (row.phones.length === 0) return false;
                  if (hideSaved && row.new_phones.length === 0) return false;
                  return true;
                });

              if (visibleRows.length === 0) {
                return (
                  <p className="text-sm text-text-secondary italic px-3 py-4 text-center border border-dashed border-[#E8E1D5] rounded">
                    {results.rows.length === 0
                      ? 'No results. Try a more specific keyword (e.g. add a city name).'
                      : results.total_phones === 0
                        ? 'No phone numbers found in any of the Google snippets. Try a different keyword that explicitly asks for "phone number" or "contact".'
                        : 'All phones from this search are already in your Contact Book. Uncheck "Hide already-saved" to view them.'}
                  </p>
                );
              }

              return visibleRows.map(({ row, originalIdx }) => (
              <div key={originalIdx} className="border border-[#E8E1D5] rounded p-3 space-y-2"
                data-testid={`google-search-row-${originalIdx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <a href={row.link} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-bold text-brand-primary hover:underline break-words">
                      {row.title || row.source}
                    </a>
                    <p className="text-[11px] text-text-secondary truncate">{row.source}</p>
                  </div>
                </div>
                {row.snippet && (
                  <p className="text-xs text-text-secondary leading-snug">{row.snippet}</p>
                )}
                {row.phones.length === 0 ? (
                  <p className="text-[11px] text-text-secondary italic flex items-center gap-1">
                    <Phone size={12} /> No phone found in the Google snippet
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {row.phones.map((phone) => {
                      const isSaved = row.already_saved.includes(phone);
                      // Hide pre-saved phone chips entirely while the
                      // "Hide already-saved" filter is on — the admin
                      // never wants to re-tick those.
                      if (hideSaved && isSaved) return null;
                      const key = `${originalIdx}:${phone}`;
                      const isSel = selected.has(key);
                      return (
                        <label key={phone}
                          className={`flex items-center gap-1.5 px-2 py-1 border rounded cursor-pointer text-xs font-mono ${
                            isSaved
                              ? 'bg-green-50 border-green-300 text-green-800 cursor-default'
                              : isSel
                                ? 'bg-brand-accent/15 border-brand-primary text-brand-primary font-bold'
                                : 'bg-white border-[#E8E1D5] text-text-primary hover:border-brand-primary/40'
                          }`}
                          data-testid={`google-search-phone-${originalIdx}-${phone}`}
                        >
                          {!isSaved && (
                            <input type="checkbox" checked={isSel}
                              onChange={() => toggleSelect(originalIdx, phone)}
                              className="cursor-pointer" />
                          )}
                          {isSaved && <CheckCircle size={12} weight="fill" className="text-green-600" />}
                          <Phone size={12} weight={isSel || isSaved ? 'fill' : 'regular'} />
                          {phone}
                          {isSaved && <span className="text-[9px] font-bold uppercase ml-1">saved</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              ));
            })()}

            {results.has_next && (
              <Button onClick={() => runSearch(true)} disabled={loading}
                variant="outline"
                className="w-full rounded-none border-brand-primary text-brand-primary hover:bg-brand-accent/10 h-10"
                data-testid="google-search-load-more-btn">
                {loading
                  ? <><Spinner size={14} className="mr-2 animate-spin" /> Loading more…</>
                  : <>+ Load more results (next page · uses 1 quota)</>}
              </Button>
            )}
            {!results.has_next && results.rows.length > 0 && (
              <p className="text-[11px] text-text-secondary text-center py-1">
                — End of Google results —
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminGoogleSearchCard;
